/**
 * RecoveryKeyValidator - Orchestrates recovery key submission and validation
 *
 * Handles the complete flow:
 * 1. Normalize and validate format
 * 2. Decode to bytes
 * 3. Verify with server
 * 4. Store in secure cache and persistent storage
 * 5. Manage rate limiting
 */

import {
  validateRecoveryKey,
  decodeRecoveryKeyString,
  verifyBackupKey,
  zeroize,
} from '../crypto/utils';
import recoveryEphemeralStore from './recoveryEphemeralStore';
import logger from '../utils/logger';

// Rate limiting configuration
const MAX_ATTEMPTS = 3;
const RATE_LIMIT_WINDOW_MS = 60000; // 1 minute
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Error codes
export const RecoveryErrors = {
  INVALID_FORMAT: 'INVALID_FORMAT',
  INVALID_RECOVERY_KEY: 'INVALID_RECOVERY_KEY',
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  PERSISTENCE_ERROR: 'PERSISTENCE_ERROR',
  TOO_MANY_ATTEMPTS: 'TOO_MANY_ATTEMPTS',
  UNKNOWN: 'UNKNOWN',
  CORRUPTED_BACKUP: 'CORRUPTED_BACKUP',
  NO_BACKUP: 'NO_BACKUP',
  CRYPTO_NOT_READY: 'CRYPTO_NOT_READY',
};

/**
 * RecoveryKeyValidator service
 */
class RecoveryKeyValidator {
  constructor() {
    this.attemptHistory = [];
    this.isSubmitting = false;
  }

  /**
   * Backwards-compatible wrapper used by UI code
   * Delegates to onSubmit with the same semantics.
   * @param {string} input
   * @param {MatrixClient} client
   * @param {Function} dispatch
   */
  async validateAndSubmit(input, client, dispatch) {
    try {
      logger.debug('RECOVERY RecoveryKeyValidator.validateAndSubmit: called');
      return await this.onSubmit(input, client, dispatch);
    } catch (err) {
      // Normalize error shape for caller
      const errorCode = this._mapError(err || {});
      logger.error('RecoveryKeyValidator.validateAndSubmit: failed', { code: errorCode, err });
      return { success: false, error: errorCode };
    }
  }

  /**
   * Check if rate limited
   * @private
   */
  _isRateLimited() {
    const now = Date.now();
    const windowStart = now - RATE_LIMIT_WINDOW_MS;

    // Filter attempts within the window
    this.attemptHistory = this.attemptHistory.filter(
      timestamp => timestamp > windowStart,
    );

    return this.attemptHistory.length >= MAX_ATTEMPTS;
  }

  /**
   * Record an attempt
   * @private
   */
  _recordAttempt() {
    this.attemptHistory.push(Date.now());
  }

  /**
   * Get remaining cooldown time
   */
  getRateLimitInfo() {
    if (!this._isRateLimited()) {
      return {
        isLimited: false,
        remainingAttempts: MAX_ATTEMPTS - this.attemptHistory.length,
        cooldownMs: 0,
      };
    }

    const oldestAttempt = Math.min(...this.attemptHistory);
    const cooldownMs = Math.max(0,
      (oldestAttempt + RATE_LIMIT_WINDOW_MS) - Date.now(),
    );

    return {
      isLimited: true,
      remainingAttempts: 0,
      cooldownMs,
    };
  }

  /**
   * Map error to recovery error code
   * @private
   */
  _mapError(error) {
    // Check for specific error codes
    if (error.code) {
      switch (error.code) {
        case 'INVALID_FORMAT':
        case 'INVALID_RECOVERY_KEY':
        case 'PERSISTENCE_ERROR':
        case 'CORRUPTED_BACKUP':
          return error.code;
        case 'SDK_UNAVAILABLE':
        case 'UNSUPPORTED_ENV':
          return RecoveryErrors.CRYPTO_NOT_READY;
        default:
          break;
      }
    }

    // Check error message patterns
    const message = error.message?.toLowerCase() || '';
    if (message.includes('network') || message.includes('timeout')) {
      return RecoveryErrors.NETWORK_TIMEOUT;
    }
    if (message.includes('quota') || message.includes('storage')) {
      return RecoveryErrors.PERSISTENCE_ERROR;
    }
    if (message.includes('wrong') || message.includes('mismatch')) {
      return RecoveryErrors.INVALID_RECOVERY_KEY;
    }

    return RecoveryErrors.UNKNOWN;
  }

  /**
   * Get current session ID from auth
   * @private
   */
  _getSessionId() {
    try {
      const credsJson = localStorage.getItem('matrixCreds');
      if (!credsJson) {
        return null;
      }

      const creds = JSON.parse(credsJson);
      return `${creds.userId}:${creds.deviceId}`;
    } catch {
      return null;
    }
  }

  /**
   * Submit recovery key for validation and storage
   * @param {string} input - User-entered recovery key
   * @param {MatrixClient} client - Matrix client for verification
   * @param {Function} dispatch - State dispatcher for UI updates
   */
  async onSubmit(input, client, dispatch) {
    // Check if already submitting
    if (this.isSubmitting) {
      logger.debug('RecoveryKeyValidator: Submission already in progress');
      return {
        success: false,
        error: RecoveryErrors.UNKNOWN,
      };
    }

    // Check rate limiting
    if (this._isRateLimited()) {
      const info = this.getRateLimitInfo();
      logger.warn('RecoveryKeyValidator: Rate limited', info);

      if (dispatch) {
        dispatch({
          type: 'fail',
          error: RecoveryErrors.TOO_MANY_ATTEMPTS,
          cooldownMs: info.cooldownMs,
        });
      }

      return {
        success: false,
        error: RecoveryErrors.TOO_MANY_ATTEMPTS,
        cooldownMs: info.cooldownMs,
      };
    }

    this.isSubmitting = true;
    this._recordAttempt();

    // Dispatch validating state
    if (dispatch) {
      dispatch({ type: 'validating' });
    }

    let keyBytes = null;

    try {
      // Step 1: Validate and normalize format
      const { normalized, isFormatValid, reason } = await validateRecoveryKey(input);

      if (!isFormatValid) {
        logger.debug('RecoveryKeyValidator: Invalid format', reason);

        if (dispatch) {
          dispatch({
            type: 'fail',
            error: RecoveryErrors.INVALID_FORMAT,
            reason,
          });
        }

        return {
          success: false,
          error: RecoveryErrors.INVALID_FORMAT,
          reason,
        };
      }

      // Step 2: Decode recovery key
      try {
        logger.debug('RECOVERY RecoveryKeyValidator: Decoding recovery key, normalized length:', normalized?.length);
        keyBytes = await decodeRecoveryKeyString(normalized);
        logger.debug('RECOVERY RecoveryKeyValidator: Successfully decoded key to', keyBytes?.length, 'bytes');
      } catch (decodeError) {
        logger.error('RECOVERY RecoveryKeyValidator: Decode failed', decodeError);
        const errorCode = this._mapError(decodeError);

        if (dispatch) {
          dispatch({
            type: 'fail',
            error: errorCode,
          });
        }

        return {
          success: false,
          error: errorCode,
        };
      }

      // Step 3: Verify with server (NO early storage)
      if (!client) {
        throw new Error('Matrix client not provided');
      }

      logger.debug('RECOVERY RecoveryKeyValidator: Starting server verification with key bytes:', keyBytes?.length, 'bytes');
      const isValid = await verifyBackupKey(client, keyBytes);
      logger.debug('RECOVERY RecoveryKeyValidator: Server verification result:', isValid);

      if (!isValid) {
        logger.debug('RECOVERY RecoveryKeyValidator: Server verification failed');

        // Zeroize key bytes
        zeroize(keyBytes);
        keyBytes = null;

        // Clear ephemeral store since key is invalid
        recoveryEphemeralStore.clear();

        if (dispatch) {
          dispatch({
            type: 'fail',
            error: RecoveryErrors.INVALID_RECOVERY_KEY,
          });
        }

        return {
          success: false,
          error: RecoveryErrors.INVALID_RECOVERY_KEY,
        };
      }

      logger.debug('RECOVERY RecoveryKeyValidator: Key verified successfully');

      // Step 4: Store in secure cache (ephemeral) - ONLY AFTER SUCCESSFUL VERIFICATION
      try {
        // Ensure ephemeral store is initialized
        if (!recoveryEphemeralStore.initialized) {
          await recoveryEphemeralStore.init();
        }

        // Store in ephemeral cache with TTL
        recoveryEphemeralStore.setKey(keyBytes);
        await recoveryEphemeralStore.persistToSession(CACHE_TTL_MS);

        logger.debug('RecoveryKeyValidator: Stored in ephemeral cache');
      } catch (cacheError) {
        logger.error('RecoveryKeyValidator: Failed to cache key', cacheError);
        // Continue - cache failure is not fatal
      }

      // Step 5: Zeroize plaintext after ephemeral storage
      // Note: We only use ephemeral storage for security - recovery keys should never be persisted
      zeroize(keyBytes);
      keyBytes = null;

      // Step 6: Dispatch success
      if (dispatch) {
        dispatch({ type: 'restore' });
      }

      // Clear attempt history on success
      this.attemptHistory = [];

      return {
        success: true,
      };

    } catch (error) {
      logger.error('RecoveryKeyValidator: Submission failed', error);

      // Ensure keyBytes is zeroized
      if (keyBytes) {
        zeroize(keyBytes);
      }

      // Clear ephemeral store on any failure
      recoveryEphemeralStore.clear();

      // Map error to appropriate code
      const errorCode = this._mapError(error);

      if (dispatch) {
        dispatch({
          type: 'fail',
          error: errorCode,
        });
      }

      return {
        success: false,
        error: errorCode,
      };

    } finally {
      this.isSubmitting = false;
    }
  }

  /**
   * Attempt to rehydrate recovery key from ephemeral storage
   * Note: Recovery keys are only stored in ephemeral memory for security
   * @param {string} sessionId - Optional session ID (not used anymore)
   */
  async rehydrate(sessionId = null) {
    // Only check ephemeral storage - we don't persist recovery keys
    if (recoveryEphemeralStore.hasKey()) {
      logger.debug('RecoveryKeyValidator: Key exists in ephemeral storage');
      return true;
    }

    logger.debug('RecoveryKeyValidator: No key in ephemeral storage');
    return false;
  }

  /**
   * Clear recovery key from ephemeral storage
   * @param {string} sessionId - Optional session ID (not used anymore)
   */
  clear(sessionId = null) {
    // Clear ephemeral store only
    recoveryEphemeralStore.clear();

    // Reset attempt history
    this.attemptHistory = [];

    logger.debug('RecoveryKeyValidator: Cleared recovery key from ephemeral storage');
  }

  /**
   * Check if recovery key exists in ephemeral storage
   * @param {string} sessionId - Optional session ID (not used anymore)
   */
  hasKey(sessionId = null) {
    // Only check ephemeral store - we don't persist recovery keys
    return recoveryEphemeralStore.hasKey();
  }
}

// Create singleton instance
const recoveryKeyValidator = new RecoveryKeyValidator();

export default recoveryKeyValidator;
